import fs from "fs/promises";
import { Dirent } from "fs";
import path from "path";

import { pathExists } from "fs-extra";
import pLimit from "p-limit";

import { readFormConfig } from "../BuildTasks/ResolveFormInfo";
import { extractGfvFromFormPath } from "../../../Common/ExtractGFVPath/ExtractGFVPath";
import { WellKnownDirectories } from "../../../Common/WellKnownDirectories";

import { FormLocationInfo } from "./FormLocationInfo";

const GLOB_SPECIFIER_UI_TESTS = ":with-ui-tests";
const GLOB_SPECIFIER_CI_BUILD = ":with-ci-build";
const GLOB_SPECIFIERS = [GLOB_SPECIFIER_UI_TESTS, GLOB_SPECIFIER_CI_BUILD] as const;
const MAX_ABSENT_GLOBS_TO_LOG = 10;

function buildGfvRegex(formGlob: string): RegExp {
    const pattern = formGlob
        .replace(GLOB_SPECIFIER_UI_TESTS, "")
        .replace(GLOB_SPECIFIER_CI_BUILD, "")
        .replace(/\*/gi, "\\d*")
        .replace(/\?/gi, "\\d");
    return new RegExp(`^${pattern}$`);
}

export interface GfvInfo {
    gfv: string;
    formPath: string;
    formRelativePath: string;
}

export class FormGlobs {
    public static async enumerateFormsInRepository(...repositoryRoots: string[]): Promise<GfvInfo[]> {
        const formsByRepos = await Promise.all(
            repositoryRoots.map(async root => {
                const formsRoot = path.join(root, "forms");
                const formJsonPaths = await enumerateCandyForms(formsRoot);
                return formJsonPaths.map(p => buildGfvInfo(p, formsRoot));
            })
        );
        return formsByRepos.flat();
    }

    public static async resolveGfvGlobs(
        formGlobs: string[],
        farmDirectory: string,
        throwErrorIfUnusedGlobFound: boolean = true
    ): Promise<FormLocationInfo[]> {
        const result: Map<string, string> = new Map();
        const foundGlobs = new Set<string>();

        const forms = await FormGlobs.enumerateFormsInRepository(path.normalize(path.join(farmDirectory, "..")));
        for (const { gfv, formPath } of forms) {
            for (const formGlob of formGlobs) {
                if (await FormGlobs.isFormMatchToGlob(formGlob, gfv, formPath)) {
                    result.set(gfv, formPath);
                    foundGlobs.add(formGlob);
                }
            }
        }

        if (throwErrorIfUnusedGlobFound) {
            const absentGlobs = Iterator.from(formGlobs)
                .filter(formGlob => !foundGlobs.has(formGlob))
                .take(MAX_ABSENT_GLOBS_TO_LOG)
                .toArray();
            if (absentGlobs.length > 0) {
                throw new Error(`Форм соответствующих ${absentGlobs.join(", ")} не найдено.`);
            }
        }

        return [...result.entries()].map(([gfv, formPath]) => ({ gfv: gfv, formPath: formPath }));
    }

    public static async resolveSpecifiers(formGlobsWithSpecifiers: string[], farmDirectory: string): Promise<string[]> {
        const results = await Promise.all(
            formGlobsWithSpecifiers.map(x => this.resolveOnlyFormGlobSpecifiers(x, farmDirectory))
        );

        return results.flat();
    }

    private static async isFormMatchToGlob(formGlob: string, gfv: string, formPath: string) {
        if (!buildGfvRegex(formGlob).test(gfv)) {
            return false;
        }
        if (formGlob.includes(GLOB_SPECIFIER_UI_TESTS)) {
            if (!(await pathExists(path.join(formPath, "test")))) {
                return false;
            }
        }
        if (formGlob.includes(GLOB_SPECIFIER_CI_BUILD)) {
            const formJsonPath = path.join(formPath, WellKnownDirectories.FormJsonFileName);
            const formConfig = await readFormConfig(formJsonPath);
            if (formConfig.build !== "ci") {
                return false;
            }
        }
        return true;
    }

    private static async resolveOnlyFormGlobSpecifiers(formGlob: string, farmDirectory: string): Promise<string[]> {
        if (GLOB_SPECIFIERS.some(s => formGlob.endsWith(s))) {
            return (await this.resolveGfvGlobs([formGlob], farmDirectory)).map(x => x.gfv);
        }
        return [formGlob];
    }
}

type ConcurrencyLimit = ReturnType<typeof pLimit>;

// Глубокого исследования за этим числом нет, при необходимости, можно смело менять
const FORM_WALK_CONCURRENCY = 32;
const GFV_DIR_NAME_REGEX = /^\d{6,}$/;

const enumerateCandyForms = (formsRoot: string): Promise<string[]> =>
    walkCandyForms(formsRoot, formsRoot, pLimit(FORM_WALK_CONCURRENCY));

async function walkCandyForms(dir: string, baseDir: string, limit: ConcurrencyLimit): Promise<string[]> {
    const entries = await readDirSafe(dir, limit);
    const nested = await Promise.all(
        entries
            .values()
            .filter(isTraversableSubdirectory)
            .map(entry => {
                const childDir = path.join(dir, entry.name);
                return GFV_DIR_NAME_REGEX.test(entry.name)
                    ? collectCandyFormJson(childDir, baseDir, limit)
                    : walkCandyForms(childDir, baseDir, limit);
            })
    );
    return nested.flat();
}

async function readDirSafe(dir: string, limit: ConcurrencyLimit): Promise<Dirent[]> {
    try {
        return await limit(() => fs.readdir(dir, { withFileTypes: true }));
    } catch {
        return [];
    }
}

function isTraversableSubdirectory(entry: Dirent): boolean {
    return entry.isDirectory() && !entry.name.startsWith(".") && entry.name !== "node_modules";
}

async function collectCandyFormJson(dir: string, baseDir: string, limit: ConcurrencyLimit): Promise<string[]> {
    const candyDir = WellKnownDirectories.CandySubdirectoryName;
    const formJsonAbsPath = path.join(dir, candyDir, WellKnownDirectories.FormJsonFileName);
    if (!(await limit(() => pathExists(formJsonAbsPath)))) {
        return [];
    }
    return [path.join(path.relative(baseDir, dir), candyDir, WellKnownDirectories.FormJsonFileName)];
}

const buildGfvInfo = (formJsonPath: string, baseDir: string): GfvInfo => {
    const formRelativePath = path.dirname(formJsonPath);
    return {
        gfv: extractGfvFromFormPath(formRelativePath),
        formPath: path.join(baseDir, formRelativePath),
        formRelativePath: formRelativePath,
    };
};
